import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  min-height: 100vh;
  padding-top: 6rem;
  position: relative;
`;


const Section = styled.section`
  position: relative;
  padding: 4rem 1.5rem;
  background: ${({ theme }) => theme.colors.background};
`;

const SectionInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  gap: 3rem;
`;

const SectionHeader = styled.div`
  max-width: 820px;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  text-align: center;
  margin: 0 auto;
`;

const Eyebrow = styled.span`
  text-transform: uppercase;
  letter-spacing: 0.3em;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.highlight};
`;

const SectionTitle = styled.h1`
  font-size: clamp(1.8rem, 3vw, 2.5rem);
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 1rem;
`;

const SectionCopy = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.white};
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  margin-bottom: 2rem;
  align-self: flex-start;

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
  }
`;

const BulkActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1rem;
  background: ${({ theme }) => theme.colors.panelBackground};
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.panelBorder};
`;

const BulkButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  background: ${({ variant, theme }) => 
    variant === 'secondary' ? 'transparent' : theme.colors.accent};
  color: ${({ variant, theme }) => 
    variant === 'secondary' ? theme.colors.accent : theme.colors.white};
  border: ${({ variant, theme }) => 
    variant === 'secondary' ? `1px solid ${theme.colors.accent}` : 'none'};
  padding: 0.6rem 1.2rem;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ variant, theme }) => 
      variant === 'secondary' ? theme.colors.accent : theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const MaterialsList = styled.div`
  display: grid;
  gap: 1rem;
`;

const MaterialRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  gap: 1rem;
  align-items: center;
  background: ${({ theme }) => theme.colors.cardBackground};
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  box-shadow: ${({ theme }) => theme.colors.cardShadow};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 768px) {
    grid-template-columns: auto 1fr;
    gap: 1rem;
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: ${({ theme }) => theme.colors.accent};
`;

const MaterialInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const MaterialName = styled.h3`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const MaterialMeta = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const StatusBadge = styled.span<{ status: 'available' | 'read-only' | 'coming-soon' }>`
  display: inline-block;
  padding: 0.3rem 0.8rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: ${({ status }) => {
    switch (status) {
      case 'available': return '#D1FAE5';
      case 'read-only': return '#FEF3C7';
      case 'coming-soon': return '#E5E7EB';
      default: return '#E5E7EB';
    }
  }};
  color: ${({ status }) => {
    switch (status) {
      case 'available': return '#065F46';
      case 'read-only': return '#92400E';
      case 'coming-soon': return '#4B5563';
      default: return '#4B5563';
    }
  }};
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  background: ${({ variant, theme }) => 
    variant === 'secondary' ? 'transparent' : theme.colors.accent};
  color: ${({ variant, theme }) => 
    variant === 'secondary' ? theme.colors.accent : theme.colors.white};
  border: ${({ variant, theme }) => 
    variant === 'secondary' ? `1px solid ${theme.colors.accent}` : 'none'};
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ variant, theme }) => 
      variant === 'secondary' ? theme.colors.accent : theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    grid-column: 1 / -1;
    justify-self: start;
  }
`;

const MobileActions = styled.div`
  display: none;
  gap: 0.5rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    display: flex;
  }
`;

interface Material {
  id: string;
  name: string;
  type: 'pdf' | 'audio' | 'video' | 'worksheet';
  status: 'available' | 'read-only' | 'coming-soon';
  fileSize?: string;
  category: string;
  fileName?: string;
}

const EducationalMaterials: React.FC = () => {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedMaterials, setSelectedMaterials] = useState<Set<string>>(new Set());

  // Load materials from API
  useEffect(() => {
    loadMaterials();
  }, []);

  const loadMaterials = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/educational-materials.php?exclude=wisdom');
      const data = await response.json();
      
      if (data.success) {
        setMaterials(data.materials);
      } else {
        setError(data.message || 'Failed to load materials');
      }
    } catch (err) {
      setError('Unable to connect to server. Please check your connection.');
      console.error('Error loading materials:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMaterial = (id: string) => {
    const newSelected = new Set(selectedMaterials);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedMaterials(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedMaterials.size === materials.length) {
      setSelectedMaterials(new Set());
    } else {
      setSelectedMaterials(new Set(materials.map(m => m.id)));
    }
  };

  const handleView = (material: Material) => {
    if (material.status === 'coming-soon') return;
    
    const viewUrl = `/api/download-material.php?id=${material.id}&action=view`;
    window.open(viewUrl, '_blank');
  };

  const handleDownload = (material: Material) => {
    if (material.status !== 'available') return;
    
    const downloadUrl = `/api/download-material.php?id=${material.id}&action=download`;
    window.open(downloadUrl, '_blank');
  };

  const handleBulkView = () => {
    selectedMaterials.forEach(id => {
      const material = materials.find(m => m.id === id);
      if (material && material.status !== 'coming-soon') {
        setTimeout(() => handleView(material), 100 * Array.from(selectedMaterials).indexOf(id));
      }
    });
  };

  const handleBulkDownload = () => {
    selectedMaterials.forEach(id => {
      const material = materials.find(m => m.id === id);
      if (material && material.status === 'available') {
        setTimeout(() => handleDownload(material), 200 * Array.from(selectedMaterials).indexOf(id));
      }
    });
  };

  const selectedCount = selectedMaterials.size;

  return (
    <PageWrapper>
      <Section>
        <SectionInner>
          <BackButton to="/#resources">
            ← Back to Resources
          </BackButton>
          
          <SectionHeader>
            <Eyebrow>Educational Resources</Eyebrow>
            <SectionTitle>Educational Materials Library</SectionTitle>
            <SectionCopy>
              Select materials to view or download. Use checkboxes to select multiple items for bulk actions.
            </SectionCopy>
          </SectionHeader>


          {/* Loading State */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
              <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>📚</div>
              <h3>Loading Educational Materials...</h3>
              <p>Please wait while we fetch the latest resources.</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div style={{ 
              background: '#fee2e2', 
              color: '#991b1b', 
              padding: '1.5rem', 
              borderRadius: '12px', 
              marginBottom: '2rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⚠️</div>
              <h3>Unable to Load Materials</h3>
              <p>{error}</p>
              <button 
                onClick={loadMaterials}
                style={{
                  background: '#10B981',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  marginTop: '1rem',
                  cursor: 'pointer'
                }}
              >
                Try Again
              </button>
            </div>
          )}

          {/* Content (only show when not loading and no error) */}
          {!loading && !error && (
            <>
              <BulkActions>
            <BulkButton onClick={handleSelectAll}>
              {selectedMaterials.size === materials.length ? 'Deselect All' : 'Select All'}
            </BulkButton>
            
            {selectedCount > 0 && (
              <>
                <span style={{ color: '#6B7280' }}>
                  {selectedCount} selected
                </span>
                <BulkButton onClick={handleBulkView} disabled={selectedCount === 0}>
                  View Selected
                </BulkButton>
                <BulkButton variant="secondary" onClick={handleBulkDownload} disabled={selectedCount === 0}>
                  Download Selected
                </BulkButton>
              </>
            )}
          </BulkActions>

          <MaterialsList>
            {materials.map((material) => (
              <MaterialRow key={material.id}>
                <Checkbox
                  type="checkbox"
                  checked={selectedMaterials.has(material.id)}
                  onChange={() => handleSelectMaterial(material.id)}
                />
                
                <MaterialInfo>
                  <MaterialName>{material.name}</MaterialName>
                  <MaterialMeta>
                    <span><strong>Category:</strong> {material.category}</span>
                    {material.fileSize && <span><strong>Size:</strong> {material.fileSize}</span>}
                    <StatusBadge status={material.status}>
                      {material.status === 'read-only' ? 'Preview Only' : 
                       material.status === 'coming-soon' ? 'Coming Soon' : 'Available'}
                    </StatusBadge>
                  </MaterialMeta>
                </MaterialInfo>
                
                <ActionButton 
                  onClick={() => handleView(material)}
                  disabled={material.status === 'coming-soon'}
                  style={{ display: window.innerWidth <= 768 ? 'none' : 'block' }}
                >
                  View
                </ActionButton>
                
                <ActionButton 
                  variant="secondary"
                  onClick={() => handleDownload(material)}
                  disabled={material.status !== 'available'}
                  style={{ display: window.innerWidth <= 768 ? 'none' : 'block' }}
                >
                  Download
                </ActionButton>

                <MobileActions>
                  <ActionButton 
                    onClick={() => handleView(material)}
                    disabled={material.status === 'coming-soon'}
                  >
                    View
                  </ActionButton>
                  
                  {material.status === 'available' && (
                    <ActionButton 
                      variant="secondary"
                      onClick={() => handleDownload(material)}
                    >
                      Download
                    </ActionButton>
                  )}
                </MobileActions>
              </MaterialRow>
            ))}
          </MaterialsList>
          </>
          )}
        </SectionInner>
      </Section>
    </PageWrapper>
  );
};

export default EducationalMaterials;